/**
 * 长图出图等待页(2026-08-03)。
 *
 * ═══ 设计口径 ═══════════════════════════════════════════════════════════════
 * 长图 RUNNING 期间后端零逐块信号(ai gather 全并发、渲完一次性上传)——所以这页
 * **不发明进度**:没有百分比、没有「正在画第 N 块」(说了就是编)。能诚实给的三样:
 *   1. 阶段(排队中/出图中)+ 已用时 + 「通常 2~4 分钟」的口径预期;
 *   2. 骨架墙:本机草稿快照对得上这单(markGenerated 落的)→ 按真实规划逐块搭,
 *      块名/徽章/「AI 出图|模板渲染」标全来自规划,高度按 estimateBlockHeight 等比 ——
 *      用户等的时候就能看到「即将出的那张长什么样」;对不上 → 通用骨架,不编块名;
 *   3. 取消口径写明白:排队中给退路(右上角取消),开始出图后明说不能取消
 *      (2026-08-03 取消收窄的另一半:按钮消失得有句话交代,否则像 bug)。
 *
 * 动效复用 .fx-slot-wait(同相位微光,reduced-motion 兜底现成);唯一新增
 * .fx-progress-sweep 不确定进度条 —— 完成即整组件卸载,不会永动到成品页。
 */
import { useEffect, useMemo, useState } from "react";

import { Loader2, Timer } from "lucide-react";

import { Card, CardContent } from "@/shared/ui/card";

import { readGeneratingView } from "../model/generatingView";

/** 骨架墙高度换算:750 设计空间 → 屏上 px(hero 3:4≈1000 → 180px),钳到可读区间。 */
function wallHeight(designH: number): number {
  return Math.max(44, Math.min(220, Math.round(designH * 0.18)));
}

function useElapsedSeconds(createdAt?: string): number | null {
  const start = useMemo(() => {
    if (!createdAt) return null;
    const t = new Date(createdAt).getTime();
    return Number.isNaN(t) ? null : t;
  }, [createdAt]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (start == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [start]);
  if (start == null) return null;
  return Math.max(0, Math.floor((now - start) / 1000));
}

function fmtElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** 没有本机快照时的通用骨架(块数未知,不写 N 不编名)。 */
const FALLBACK_HEIGHTS = [180, 72, 140, 96, 64];

export function LongImageGeneratingPanel({
  taskId,
  status,
  createdAt,
}: {
  taskId: number;
  status: "PENDING" | "RUNNING";
  createdAt?: string;
}) {
  // 快照是提交那一刻落盘的,不会中途变 —— 读一次就够,别每次轮询都碰 localStorage
  const view = useMemo(() => readGeneratingView(taskId), [taskId]);
  const elapsed = useElapsedSeconds(createdAt);
  const queued = status === "PENDING";

  return (
    <div data-testid="li-generating-panel" className="flex flex-col gap-3">
      {/* ── 阶段条 ── */}
      <Card>
        <CardContent className="flex flex-col gap-2 p-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm font-medium">
              {queued ? "排队中,马上开始" : view?.engineCount === 0 ? "模板渲染中" : "AI 出图中"}
            </span>
            {elapsed != null && (
              <span className="ml-auto flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                <Timer className="h-3.5 w-3.5" />
                已用时 {fmtElapsed(elapsed)}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {/* 零引擎块的任务几十秒就完,不许对它说「AI 出图 · 2~4 分钟」(2026-08-10 nit) */}
            {view
              ? view.engineCount === 0
                ? `全部 ${view.blocks.length} 块由模板渲染,不走 AI 出图 · 一般几十秒内完成。`
                : `${view.engineCount} 个图块${queued ? "将由" : "正在"} AI 出图,其余 ${
                    view.blocks.length - view.engineCount
                  } 块由模板渲染 · 通常 2~4 分钟。`
              : "图块由 AI 出图,文字/数据块由模板渲染 · 含 AI 图块时通常 2~4 分钟。"}
            {queued ? "还没开始出图,现在取消不扣积分。" : "已开始出图,本阶段不能取消。"}
          </p>
          {/* 不确定进度:一道横扫光,不假装知道百分比 */}
          <div className="relative h-1 overflow-hidden rounded-full bg-muted">
            <div className="fx-progress-sweep absolute inset-y-0 w-1/3 rounded-full bg-primary/60" />
          </div>
        </CardContent>
      </Card>

      {/* ── 骨架墙:窄列居中,长得像即将出的那张竖长图 ── */}
      <div className="mx-auto flex w-full max-w-sm flex-col gap-1.5">
        {view
          ? view.blocks.map((b) => (
              <div
                key={b.id}
                data-testid="li-gen-block"
                className="fx-slot-wait flex items-start justify-between rounded-md border border-border/60 bg-muted/50 px-3 py-2"
                style={{ height: wallHeight(b.height) }}
              >
                <span className="flex min-w-0 items-center gap-1.5 text-xs text-foreground/70">
                  <span className="truncate font-medium">{b.name}</span>
                  <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-2xs text-muted-foreground">
                    {b.badge}
                  </span>
                </span>
                <span
                  className={
                    b.engine
                      ? "shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-2xs font-medium text-primary"
                      : "shrink-0 rounded bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground"
                  }
                >
                  {b.engine ? "AI 出图" : "模板渲染"}
                </span>
              </div>
            ))
          : FALLBACK_HEIGHTS.map((h, i) => (
              <div
                key={i}
                data-testid="li-gen-block-fallback"
                className="fx-slot-wait rounded-md border border-border/60 bg-muted/50"
                style={{ height: h }}
              />
            ))}
      </div>
    </div>
  );
}
